// Navbar component with live gradient and bordered nav links
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import {
  HomeOutlined as HomeIcon,
  AssignmentOutlined as AssignmentIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LiveHelpOutlined as HelpIcon,
} from "@material-ui/icons";
import {
  NoteAltOutlined as NoteIcon,
  CloseRounded as CloseIcon,
  AccountCircleOutlined as AccountIcon,
  VideoCallOutlined as VideoIcon,
  LogoutRounded as LogoutIcon,
} from "@mui/icons-material";

import { AuthContext } from "../../context/authContext/AuthContext";
import { logoutUser } from "../../context/authContext/apiCalls";
import dummyProfilePic from "../../assets/dummyProfilePic.png";
import "./style.scss";
import { MaterialsContext } from "../../context/materialsContext/MaterialsContext";
import { TasksContext } from "../../context/tasksContext/TasksContext";
import { DoubtsContext } from "../../context/doubtsContext/DoubtsContext";
import { getRecentMaterials } from "../../context/materialsContext/apiCalls";
import { getRecentTasks } from "../../context/tasksContext/apiCalls";
import { getRecentDoubts } from "../../context/doubtsContext/apiCalls";

const Navbar = () => {
  const location = useLocation();
  const { user, dispatch } = useContext(AuthContext);
  const { recentMaterials, dispatch: materialsDispatch } = useContext(MaterialsContext);
  const { recentTasks, dispatch: tasksDispatch } = useContext(TasksContext);
  const { recentDoubts, dispatch: doubtsDispatch } = useContext(DoubtsContext);
  const [profileOptionsDialogOpened, setProfileOptionsDialogOpened] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  // Set active nav link based on route
  useEffect(() => {
    let currentTab = location.pathname.split("/")[1] || "home";
    if (currentTab !== "home" && currentTab.slice(-1) !== "s") currentTab += "s";
    const tabToSetActive = document.querySelector(`.link#${currentTab}`);
    if (tabToSetActive) tabToSetActive.classList.add("active");
  }, [location.pathname]);

  // Initialize last-seen markers for student accounts only
  useEffect(() => {
    if (!user || user.isTeacher || user.isAdmin) return;
    const keys = ["material", "task", "doubt"];
    keys.forEach((k) => {
      const key = `lastSeen_${k}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, String(Date.now()));
      }
    });
  }, [user]);

  // Ensure we have recent items for computing badges
  useEffect(() => {
    if (!user || user.isTeacher || user.isAdmin) return; // badges for students only
    if (!recentMaterials || recentMaterials.length === 0) getRecentMaterials(user, materialsDispatch);
    if (!recentTasks || recentTasks.length === 0) getRecentTasks(user, tasksDispatch);
    if (!recentDoubts || recentDoubts.length === 0) getRecentDoubts(user, doubtsDispatch);
  }, [user, recentMaterials?.length, recentTasks?.length, recentDoubts?.length, materialsDispatch, tasksDispatch, doubtsDispatch]);

  // Chat unread counter via localStorage (applies to both roles)
  const getChatUnreadCount = (uid) => {
    if (!uid) return 0;
    try {
      const arr = JSON.parse(localStorage.getItem(`chat_unread_${uid}`) || "[]");
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  };
  useEffect(() => {
    const update = () => setChatUnreadCount(getChatUnreadCount(user?._id));
    update();
    window.addEventListener('storage', update);
    window.addEventListener('chat-unread-changed', update);
    return () => {
      window.removeEventListener('storage', update);
      window.removeEventListener('chat-unread-changed', update);
    };
  }, [user?._id]);

  const getLastSeen = (k) => {
    const v = localStorage.getItem(`lastSeen_${k}`);
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const latestCreatedAt = (list) => {
    if (!Array.isArray(list) || list.length === 0) return 0;
    return list.reduce((mx, it) => {
      const t = it?.createdAt ? new Date(it.createdAt).getTime() : 0;
      return t > mx ? t : mx;
    }, 0);
  };

  const hasNewMaterials = useMemo(() => {
    if (user?.isTeacher || user?.isAdmin) return false;
    const last = getLastSeen("material");
    const latest = latestCreatedAt(recentMaterials);
    return latest > last;
  }, [user?.isTeacher, user?.isAdmin, recentMaterials]);

  const hasNewTasks = useMemo(() => {
    if (user?.isTeacher || user?.isAdmin) return false;
    const last = getLastSeen("task");
    const latest = latestCreatedAt(recentTasks);
    return latest > last;
  }, [user?.isTeacher, user?.isAdmin, recentTasks]);

  const hasNewDoubts = useMemo(() => {
    if (user?.isTeacher || user?.isAdmin) return false;
    const last = getLastSeen("doubt");
    const latest = latestCreatedAt(recentDoubts);
    return latest > last;
  }, [user?.isTeacher, user?.isAdmin, recentDoubts]);

  const markSeen = (k) => {
    if (!user || user.isTeacher || user.isAdmin) return;
    localStorage.setItem(`lastSeen_${k}`, String(Date.now()));
  };

  // Auto-mark seen when user navigates into section routes
  useEffect(() => {
    if (!user || user.isTeacher || user.isAdmin) return;
    const p = location.pathname;
    if (p.startsWith("/materials")) markSeen("material");
    if (p.startsWith("/tasks")) markSeen("task");
    if (p.startsWith("/doubts")) markSeen("doubt");
    // Home route shouldn't clear anything
  }, [location.pathname]);

  return (
    <div className="navbar">
      <div className="nav-container">
        {/* Left Logo Section */}
        <div className="left">
          <Link to="/" className="link" aria-label="SmartClass Home">
            <img src="/logo.png" alt="SmartClass" className="logo-img" />
          </Link>
        </div>

        {/* Center Nav Links */}
        <div className="middle">
          <div className="nav-links">
            <Link to="/" className="link" id="home">
              <HomeIcon className="icon" />
              <span>Home</span>
              <div className="navlink-border"></div>
            </Link>
            <Link to="/materials/all" className="link" id="materials" onClick={() => markSeen("material") }>
              <NoteIcon className="icon" />
              {hasNewMaterials && <span className="badge-dot" aria-label="new materials" />}
              <span>Subject Matter</span>
              <div className="navlink-border"></div>
            </Link>
            <Link to="/tasks/all" className="link" id="tasks" onClick={() => markSeen("task") }>
              <AssignmentIcon className="icon" />
              {hasNewTasks && <span className="badge-dot" aria-label="new assignments" />}
              <span>Assignments</span>
              <div className="navlink-border"></div>
            </Link>
            <Link to="/doubts/all" className="link" id="doubts" onClick={() => markSeen("doubt") }>
              <HelpIcon className="icon" />
              {(chatUnreadCount > 0 || hasNewDoubts) && <span className="badge-dot" aria-label="new queries" />}
              <span>Queries</span>
              <div className="navlink-border"></div>
            </Link>
          </div>
        </div>

        {/* Right Profile Section */}
        <div className="right">
          <div className="profile" onClick={() => setProfileOptionsDialogOpened(true)}>
            <img src={user?.profilePic || dummyProfilePic} alt="profile" />
            <div className="userDetails">
              <span>{user?.fullname}</span>
              <span>{user?.isTeacher ? "Teacher" : "Student"}</span>
            </div>
            <ArrowDropDownIcon className="icon" />
          </div>

          {/* Profile Options Dialog */}
          <Dialog
            onClose={() => setProfileOptionsDialogOpened(false)}
            open={profileOptionsDialogOpened}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle disableTypography style={{ display: "flex", justifyContent: "center" }}>
              <IconButton
                onClick={() => setProfileOptionsDialogOpened(false)}
                style={{ position: "absolute", left: "10px", color: "var(--text-color2)" }}
              >
                <CloseIcon style={{ fontSize: "26px" }} />
              </IconButton>
              <h3 style={{ color: "var(--primary-color)" }}>SmartClass</h3>
            </DialogTitle>
            <Divider />

            {/* Navigation Items Inside Dialog */}
            <List>
              <Link to="/userupdate" className="link">
                <ListItem button onClick={() => setProfileOptionsDialogOpened(false)}>
                  <AccountIcon style={{ fontSize: "26px" }} />
                  <ListItemText primary="Update Profile" />
                </ListItem>
              </Link>

              <Link to="/materials/all" className="link">
                <ListItem button onClick={() => setProfileOptionsDialogOpened(false)}>
                  <NoteIcon style={{ fontSize: "26px" }} />
                  <ListItemText primary="All Materials" />
                </ListItem>
              </Link>

              <Link to="/tasks/all" className="link">
                <ListItem button onClick={() => setProfileOptionsDialogOpened(false)}>
                  <AssignmentIcon style={{ fontSize: "26px" }} />
                  <ListItemText primary="All Tasks" />
                </ListItem>
              </Link>

              <Link to="/doubts/all" className="link">
                <ListItem button onClick={() => setProfileOptionsDialogOpened(false)}>
                  <HelpIcon style={{ fontSize: "26px" }} />
                  <ListItemText primary="All Doubts" />
                </ListItem>
              </Link>

              <Link to="/createclass" className="link">
                <ListItem button onClick={() => setProfileOptionsDialogOpened(false)}>
                  <VideoIcon style={{ fontSize: "26px" }} />
                  <ListItemText primary="Create Meeting" />
                </ListItem>
              </Link>

              <Divider />
              <ListItem
                button
                onClick={() => {
                  setProfileOptionsDialogOpened(false);
                  logoutUser(dispatch);
                }}
              >
                <LogoutIcon style={{ fontSize: "26px" }} />
                <ListItemText primary="Logout" />
              </ListItem>
            </List>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
